import moment from 'moment';
import DatePreset from 'App/services/datePresetService';
import { FilterConfigRaw } from 'App/babel/filterConfigs/FilterConfig';

type DateValue = { start: Date | string | null; end: Date | string | null; preset: string };

export type PersonalEmailValue = {
	has: boolean;
	Date: { value: Partial<DateValue> };
	User: { value: number[] };
};

type PersonalEmailFilter = { inactive?: boolean; value: PersonalEmailValue };

/** ui-3 saves a custom range as `{ start, end }` with no preset, and a preset without the range. */
export const dateOf = ({ start = null, end = null, preset }: Partial<DateValue>): DateValue => ({
	start,
	end,
	preset: preset ?? (start || end ? 'custom' : 'whenever')
});

const isDateSet = (date: DateValue) =>
	date.preset === 'custom' ? !!(date.start || date.end) : date.preset !== 'whenever';

export const newFilter = (): PersonalEmailFilter => ({
	inactive: true,
	value: {
		has: true,
		Date: { value: { start: null, end: null, preset: 'whenever' } },
		User: { value: [] }
	}
});

/** A one-to-one email sent to the contact - outgoing and not part of a mailing - optionally
 * within a period and by given users. All conditions sit in one group, so they must hold for
 * the same email. */
const personalEmailFilter: Required<Pick<FilterConfigRaw, 'generate' | 'isInactive' | 'toUrl' | 'fromUrl'>> & {
	getText: (value: unknown, inactive?: boolean) => number;
	build: () => NonNullable<FilterConfigRaw['build']>;
} = {
	generate: newFilter,
	isInactive: filter => !!filter.inactive,
	getText: (stored, inactive) => {
		let count = inactive === true ? 0 : 1;
		if (!stored) {
			return count;
		}
		const value = stored as PersonalEmailValue;
		if (isDateSet(dateOf(value.Date.value))) {
			count++;
		}
		if (value.User.value.length) {
			count++;
		}
		return count;
	},
	toUrl: filter => {
		const value = filter.value as PersonalEmailValue;
		const v: Record<string, unknown> = { has: value.has };
		const date = dateOf(value.Date.value);
		if (isDateSet(date)) {
			v.da = { p: date.preset, s: date.start, e: date.end };
		}
		if (value.User.value.length) {
			v.us = value.User.value;
		}
		return { v, i: filter.inactive };
	},
	fromUrl: rawFilter => {
		const filter = newFilter();
		const v = rawFilter.v;
		filter.inactive = !!rawFilter.i;
		filter.value.has = v.has;
		if (v.da) {
			filter.value.Date = {
				value: {
					start: v.da.s ? new Date(v.da.s) : null,
					end: v.da.e ? new Date(v.da.e) : null,
					preset: v.da.p
				}
			};
		}
		if (v.us) {
			filter.value.User = { value: v.us };
		}
		return filter;
	},
	build: () => (filter, rb, getField, useTags, _activeFilters, _getConfig, preserveDatePresets) => {
		if (filter.inactive) {
			return;
		}
		const value = filter.value as PersonalEmailValue;
		const group = rb.groupBuilder();

		group.addFilter(getField('mail.type'), rb.comparisonTypes.Equals, 'out');
		group.addFilter(getField('mail.mailCampaign.id'), rb.comparisonTypes.Equals, 0);

		const date = dateOf(value.Date.value);
		if (isDateSet(date)) {
			if (preserveDatePresets && date.preset !== 'custom') {
				group.addFilter(getField('mail.date'), rb.comparisonTypes.Equals, JSON.stringify(date));
			} else {
				// A custom range comes back as picked - a datepicker's local midnight - and a timestamp
				// end leaves out the end day. A bare date counts the whole day, which is what ui-3 sends.
				const dates =
					date.preset === 'custom'
						? {
								start: date.start && moment(date.start).format('YYYY-MM-DD'),
								end: date.end && moment(date.end).format('YYYY-MM-DD')
						  }
						: DatePreset.getDatesFromValue(date, useTags);
				if (dates.start) {
					group.addFilter(getField('mail.date'), rb.comparisonTypes.GreaterThanEquals, dates.start);
				}
				if (dates.end) {
					group.addFilter(getField('mail.date'), rb.comparisonTypes.LowerThanEquals, dates.end);
				}
			}
		}

		if (value.User.value.length) {
			group.addFilter(getField('mail.user.id'), rb.comparisonTypes.Equals, value.User.value);
		}

		if (!value.has) {
			group.isNotFilter();
		}
		group.done();
	}
};

export default personalEmailFilter;
